import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete input powered by Google Places (proxied via /api/maps).
 * On selection, calls onSelect({ description, place_id, lat, lng, city, state, zip_code, formatted_address }).
 *
 * Props:
 *   value, onChange, placeholder, types ("geocode" | "address" | "(cities)" | "establishment")
 *   onSelect: fn(detail)
 *   className, dataTestId
 */
export default function PlacesAutocompleteInput({
  value = "",
  onChange,
  onSelect,
  placeholder = "Search address...",
  types = "address",
  className = "",
  dataTestId = "places-autocomplete",
  inputClassName = "",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const fetchPredictions = async (q) => {
    if (!q || q.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/maps/places/autocomplete`, { params: { query: q, types } });
      setSuggestions(res.data || []);
      setOpen(true);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(v), 280);
  };

  const pick = async (s) => {
    setPicking(true);
    try {
      const res = await axios.get(`${API}/maps/places/details`, { params: { place_id: s.place_id } });
      onChange?.(res.data.formatted_address || s.description);
      onSelect?.(res.data);
      setOpen(false); setSuggestions([]);
    } catch { /* ignore */ }
    finally { setPicking(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={dataTestId}>
      <div className="relative">
        <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 text-yellow-500 pointer-events-none" size={16} />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className={inputClassName || "bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-10 py-3 text-white focus:border-yellow-500 outline-none w-full"}
          autoComplete="off"
        />
        {(loading || picking) && <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-500 animate-spin" size={16} />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-xl max-h-72 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              type="button"
              key={s.place_id}
              onClick={() => pick(s)}
              className="w-full text-right px-4 py-3 hover:bg-slate-800 border-b border-slate-800 last:border-b-0 flex items-start gap-2"
              data-testid={`pac-${s.place_id}`}
            >
              <MapPin size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{s.main_text || s.description}</p>
                {s.secondary_text && <p className="text-xs text-slate-400 truncate">{s.secondary_text}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
